import Lexxy from "../config/lexxy"
import { createElement, generateDomId, parseHtml } from "../helpers/html_helper"
import { getNonce } from "../helpers/csp_helper"
import { $createTextNode, $isTextNode, COMMAND_PRIORITY_CRITICAL, INPUT_COMMAND, KEY_ARROW_DOWN_COMMAND, KEY_ARROW_UP_COMMAND, KEY_ENTER_COMMAND, KEY_SPACE_COMMAND, KEY_TAB_COMMAND } from "lexical"
import { $textBeforeOffset } from "../helpers/lexical_helper"
import { CustomActionTextAttachmentNode } from "../nodes/custom_action_text_attachment_node"
import InlinePromptSource from "../editor/prompt/inline_source"
import DeferredPromptSource from "../editor/prompt/deferred_source"
import RemoteFilterSource from "../editor/prompt/remote_filter_source"
import { debounce, nextFrame } from "../helpers/timing_helper"
import { ListenerBin, registerEventListener } from "../helpers/listener_helper"

const NOTHING_FOUND_DEFAULT_MESSAGE = "Nothing found"
const DEFAULT_FILTER_PARAM = "filter"
const FILTER_DEBOUNCE_INTERVAL = 50
const COMPLETING_PUNCTUATION = [ ",", ".", "!", "?", ";", ":", ")", "'", "’" ]

// Start of line, or after a space or newline, optionally followed by an opening parenthesis.
const DEFAULT_ONLY_AT_PATTERN = "(?:^|[ \\n])\\(?"

export class LexicalPromptElement extends HTMLElement {
  #globalListeners = new ListenerBin()
  #popoverListeners = new ListenerBin()
  #shownFilter
  #debouncedFilterOptions = debounce(() => this.#filterOptions(), FILTER_DEBOUNCE_INTERVAL)

  constructor() {
    super()
    this.showPopoverId = 0
  }

  static observedAttributes = [ "connected" ]

  connectedCallback() {
    this.source = this.#createSource()

    this.#addTriggerListener()
    this.#removePopoverBeforeTurboCaches()
    this.toggleAttribute("connected", true)
  }

  disconnectedCallback() {
    this.#popoverListeners.dispose()
    this.#globalListeners.dispose()
    this.source = null
    this.#removePopover()
  }


  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "connected" && this.isConnected && oldValue != null && oldValue !== newValue) {
      requestAnimationFrame(() => this.#reconnect())
    }
  }

  get name() {
    return this.getAttribute("name")
  }

  get trigger() {
    return this.getAttribute("trigger")
  }

  get supportsSpaceInSearches() {
    return this.hasAttribute("supports-space-in-searches")
  }

  get onlyAt() {
    return this.getAttribute("only-at")
  }

  get verticalDirection() {
    return this.getAttribute("vertical-direction")
  }

  get open() {
    return this.popoverElement?.classList?.contains("lexxy-prompt-menu--visible")
  }

  get closed() {
    return !this.open
  }

  get #filterParam() {
    return this.getAttribute("filter-param") || DEFAULT_FILTER_PARAM
  }

  get #doesSpaceSelect() {
    return !this.supportsSpaceInSearches
  }

  #createSource() {
    const src = this.getAttribute("src")
    if (src) {
      if (this.hasAttribute("remote-filtering")) {
        return new RemoteFilterSource(src, this.#filterParam)
      } else {
        return new DeferredPromptSource(src)
      }
    } else {
      return new InlinePromptSource(this.querySelectorAll("lexxy-prompt-item"))
    }
  }

  #addTriggerListener() {
    if (!this.#promptContentTypePermitted) return

    this.#popoverListeners.track(this.#editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        if (this.#selection.isInsideCodeBlock) return

        const { node, offset } = this.#selection.selectedNodeWithOffset()
        if (!node) return

        if ($isTextNode(node)) {
          const fullText = node.getTextContent()
          const triggerLength = this.trigger.length

          // Check if we have enough characters for the trigger
          if (offset >= triggerLength) {
            const textBeforeCursor = fullText.slice(offset - triggerLength, offset)

            if (textBeforeCursor === this.trigger) {
              const textBeforeTrigger = $textBeforeOffset(node, offset - triggerLength)

              if (this.#onlyAtRegExp.test(textBeforeTrigger)) {
                this.#popoverListeners.dispose()
                this.#showPopover()
              }
            }
          }
        }
      })
    }))
  }

  get #onlyAtRegExp() {
    return new RegExp(`(?:${this.onlyAt ?? DEFAULT_ONLY_AT_PATTERN})$`)
  }

  get #promptContentTypePermitted() {
    // `insert-editable-text` prompts never create attachments, so the
    // editor's attachment support and content-type allowlist don't apply.
    if (this.hasAttribute("insert-editable-text")) return true

    const el = this.#editorElement
    if (!el.supportsAttachments) {
      return false
    } else {
      const templates = Array.from(this.querySelectorAll("template[type='editor']"))
      const types = templates.length
        ? templates.map(t => t.getAttribute("content-type") || this.#defaultPromptContentType)
        : [ this.#defaultPromptContentType ]
      return types.some(t => el.permitsAttachmentContentType(t))
    }
  }

  #addCursorPositionListener() {
    this.#popoverListeners.track(this.#editor.registerUpdateListener(({ editorState }) => {
      if (this.closed) return

      editorState.read(() => {
        if (this.#selection.isInsideCodeBlock) {
          this.#hidePopover()
          return
        }

        const { node, offset } = this.#selection.selectedNodeWithOffset()
        if (!node) return

        if (this.#cursorIsTypingSearchTerm(node, offset)) {
          if (!this.popoverElement.hasAttribute("data-anchored")) {
            this.#positionPopover()
          }
        } else {
          this.#hidePopover()
        }
      })
    }))
  }

  // The popover should stay open only while the cursor sits at the end of the
  // trigger and its search term. When the cursor moves away — before the
  // trigger, or past the token into later text — the text between the trigger
  // and the cursor breaks that run and we dismiss. A newline always breaks the
  // run; a space breaks it only for triggers that don't support spaces in
  // searches, since those that do (e.g. `person:`) expect multi-word terms.
  #cursorIsTypingSearchTerm(node, offset) {
    if (!$isTextNode(node) || offset === 0) return false

    const textBeforeCursor = node.getTextContent().slice(0, offset)
    const lastTriggerIndex = textBeforeCursor.lastIndexOf(this.trigger)
    if (lastTriggerIndex === -1) return false

    const searchTerm = textBeforeCursor.slice(lastTriggerIndex + this.trigger.length)
    const breakPattern = this.supportsSpaceInSearches ? /\n/ : /[ \n]/
    return !breakPattern.test(searchTerm)
  }

  get #editor() {
    return this.#editorElement.editor
  }

  get #editorElement() {
    return this.closest("lexxy-editor")
  }

  get #selection() {
    return this.#editorElement.selection
  }

  async #showPopover() {
    const showId = ++this.showPopoverId
    if (!this.popoverElement) {
      const listItems = await this.source.buildListItems()
      if (this.showPopoverId !== showId) return
      this.popoverElement = this.#buildPopover(listItems)
    }

    this.#resetPopoverPosition()
    await this.#filterOptions()
    if (this.showPopoverId !== showId) return

    this.popoverElement.classList.toggle("lexxy-prompt-menu--visible", true)
    this.#selectFirstOption()

    this.#popoverListeners.track(
      registerEventListener(this.#editorElement, "keydown", this.#handleKeydownOnPopover),
      registerEventListener(this.#editorElement, "lexxy:change", this.#debouncedFilterOptions)
    )

    this.#registerKeyListeners()
    this.#addCursorPositionListener()
  }

  #registerKeyListeners() {
    // We can't use a regular keydown for Enter as Lexical handles it first
    this.#popoverListeners.track(
      this.#editor.registerCommand(KEY_ENTER_COMMAND, this.#handleSelectedOption.bind(this), COMMAND_PRIORITY_CRITICAL),
      this.#editor.registerCommand(KEY_TAB_COMMAND, this.#handleSelectedOption.bind(this), COMMAND_PRIORITY_CRITICAL)
    )

    if (this.#doesSpaceSelect) {
      this.#popoverListeners.track(this.#editor.registerCommand(KEY_SPACE_COMMAND, this.#handleSelectedOption.bind(this), COMMAND_PRIORITY_CRITICAL))
      this.#popoverListeners.track(this.#editor.registerCommand(INPUT_COMMAND, this.#handleInputCommand.bind(this), COMMAND_PRIORITY_CRITICAL))
    }

    // Register arrow keys with CRITICAL priority to prevent Lexical's selection handlers from running
    this.#popoverListeners.track(
      this.#editor.registerCommand(KEY_ARROW_UP_COMMAND, this.#handleArrowUp.bind(this), COMMAND_PRIORITY_CRITICAL),
      this.#editor.registerCommand(KEY_ARROW_DOWN_COMMAND, this.#handleArrowDown.bind(this), COMMAND_PRIORITY_CRITICAL)
    )
  }

  #handleArrowUp(event) {
    this.#moveSelectionUp()
    event.preventDefault()
    return true
  }

  #handleArrowDown(event) {
    this.#moveSelectionDown()
    event.preventDefault()
    return true
  }

  #selectFirstOption() {
    const firstOption = this.#listItemElements[0]

    if (firstOption) {
      this.#selectOption(firstOption)
    }
  }

  get #listItemElements() {
    return Array.from(this.popoverElement.querySelectorAll(".lexxy-prompt-menu__item"))
  }

  #selectOption(listItem, { scrollIntoView = false } = {}) {
    this.#clearListItemSelection()
    listItem.toggleAttribute("aria-selected", true)
    if (scrollIntoView) {
      listItem.scrollIntoView({ block: "nearest", container: "nearest", behavior: "smooth" })
    }

    this.#setEditorAssociationAttribute("aria-controls", this.popoverElement.id)
    this.#setEditorAssociationAttribute("aria-activedescendant", listItem.id)
    this.#setEditorAssociationAttribute("aria-haspopup", "listbox")
  }

  #clearListItemSelection() {
    this.#listItemElements.forEach((item) => { item.toggleAttribute("aria-selected", false) })
  }

  #clearSelection() {
    this.#clearListItemSelection()
    this.#clearEditorAssociation()
  }

  #clearEditorAssociation() {
    this.#editorContentElement.removeAttribute("aria-controls")
    this.#editorContentElement.removeAttribute("aria-activedescendant")
    this.#editorContentElement.removeAttribute("aria-haspopup")
  }

  #setEditorAssociationAttribute(name, value) {
    if (this.#editorContentElement.getAttribute(name) !== value) {
      this.#editorContentElement.setAttribute(name, value)
    }
  }

  // Right after a Turbo history restore the editor reconnects before the DOM selection
  // is re-established, so the cursor geometry is momentarily unavailable. Anchoring then
  // would pin the menu to the editor's left edge for the rest of the open cycle, so we
  // skip it and let a later reposition anchor it once the selection is ready. The menu
  // stays hidden until anchored (see the `[data-anchored]` rule in the stylesheet).
  #positionPopover() {
    const cursorPosition = this.#selection.cursorPosition
    if (!cursorPosition) return

    const { x, y, fontSize } = cursorPosition
    const editorRect = this.#editorElement.getBoundingClientRect()
    const contentRect = this.#editorContentElement.getBoundingClientRect()
    const verticalOffset = contentRect.top - editorRect.top

    if (!this.popoverElement.hasAttribute("data-anchored")) {
      this.#setPopoverOffsetX(x)
      this.#setPopoverOffsetY(y + verticalOffset)
      this.popoverElement.toggleAttribute("data-anchored", true)
    }

    const popoverRect = this.popoverElement.getBoundingClientRect()

    if (popoverRect.right > editorRect.right) {
      this.popoverElement.toggleAttribute("data-clipped-at-right", true)
    }

    const forceTop = this.verticalDirection === "top"
    const forceBottom = this.verticalDirection === "bottom"

    if (!forceBottom && (forceTop || this.#fitsBetterAbove(popoverRect, fontSize))) {
      this.#setPopoverOffsetY(contentRect.height - y + fontSize)
      this.popoverElement.toggleAttribute("data-clipped-at-bottom", true)
    }
  }

  #fitsBetterAbove(popoverRect, fontSize) {
    const { top, bottom } = this.#visibleBounds()
    const overflowsBelow = popoverRect.bottom > bottom
    const roomAbove = popoverRect.top - fontSize - top
    const roomBelow = bottom - popoverRect.top

    return overflowsBelow && roomAbove > roomBelow
  }

  // A scroll container or modal around the editor clips the menu at its scrollport before the
  // window does, so the visible area is the window narrowed by every clipping ancestor. A fixed
  // ancestor escapes the clipping of everything up to its containing block: the window, or the
  // nearest ancestor that a transform, filter or containment makes contain fixed descendants.
  // A top-layer ancestor, such as a modal dialog, escapes everything outside it.
  #visibleBounds() {
    let top = 0
    let bottom = window.innerHeight
    let escapingToContainingBlock = false

    for (let node = this.#editorElement; node && node !== document.body; node = node.parentElement) {
      const style = getComputedStyle(node)

      if (style.display !== "contents" && (!escapingToContainingBlock || containsFixedDescendants(style))) {
        escapingToContainingBlock = style.position === "fixed"

        if (clipsDescendants(style)) {
          const scrollport = scrollportBounds(node)
          top = Math.max(top, scrollport.top)
          bottom = Math.min(bottom, scrollport.bottom)
        }
      }

      if (node.matches(":modal, :popover-open")) break
    }

    return { top, bottom }
  }

  #setPopoverOffsetX(value) {
    this.popoverElement.style.setProperty("--lexxy-prompt-offset-x", `${value}px`)
  }

  #setPopoverOffsetY(value) {
    this.popoverElement.style.setProperty("--lexxy-prompt-offset-y", `${value}px`)
  }

  #resetPopoverPosition() {
    this.popoverElement.removeAttribute("data-clipped-at-bottom")
    this.popoverElement.removeAttribute("data-clipped-at-right")
    this.popoverElement.removeAttribute("data-anchored")
  }

  async #hidePopover() {
    this.showPopoverId++
    this.#clearSelection()
    this.popoverElement.classList.toggle("lexxy-prompt-menu--visible", false)
    this.#popoverListeners.dispose()

    await nextFrame()
    this.#addTriggerListener()
  }

  // The popover is appended to the <lexxy-editor> subtree, so Turbo serializes it
  // into the page cache. Removing it before caching prevents an orphaned, unmanaged
  // popover from being restored on history back/forward. Turbo also caches without
  // navigating (on a same-page anchor), so the prompt must stay armed afterwards.
  #removePopoverBeforeTurboCaches() {
    this.#globalListeners.track(
      registerEventListener(document, "turbo:before-cache", this.#closeBeforeTurboCaches)
    )
  }

  #closeBeforeTurboCaches = () => {
    this.#clearEditorAssociation()
    this.#removePopover()
    this.#addTriggerListener()
  }

  #removePopover() {
    this.showPopoverId++
    this.#popoverListeners.dispose()
    this.popoverElement?.remove()
    this.popoverElement = null
  }

  #filterOptions = async () => {
    if (this.initialPrompt) {
      this.initialPrompt = false
      return
    }

    if (!this.popoverElement) return

    const showId = this.showPopoverId
    if (this.#editorContents.containsTextBackUntil(this.trigger)) {
      await this.#showFilteredOptions()
      if (this.showPopoverId !== showId) return

      // Re-check after async operation — the trigger may have been consumed
      // (e.g. markdown heading shortcut converted "# " to h1 during the fetch)
      if (!this.#editorContents.containsTextBackUntil(this.trigger)) {
        this.#hidePopover()
        return
      }

      await nextFrame()
      if (this.showPopoverId !== showId) return
      this.#positionPopover()
    } else {
      this.#hidePopover()
    }
  }

  async #showFilteredOptions() {
    const showId = this.showPopoverId
    const filter = this.#editorContents.textBackUntil(this.trigger)
    const filteredListItems = await this.source.buildListItems(filter)
    if (this.showPopoverId !== showId) return
    if (!this.#editorContents.containsTextBackUntil(this.trigger)) return

    this.popoverElement.innerHTML = ""
    this.#shownFilter = filter

    if (filteredListItems.length > 0) {
      this.#showResults(filteredListItems)
    } else {
      this.#showEmptyResults()
    }
    this.#selectFirstOption()
  }

  #showResults(filteredListItems) {
    this.popoverElement.classList.remove("lexxy-prompt-menu--empty")
    this.popoverElement.append(...filteredListItems)
  }

  #showEmptyResults() {
    this.popoverElement.classList.add("lexxy-prompt-menu--empty")
    const el = createElement("li", { textContent: this.#emptyResultsMessage })
    el.classList.add("lexxy-prompt-menu__item--empty")
    this.popoverElement.append(el)
  }

  get #emptyResultsMessage() {
    return this.getAttribute("empty-results") || NOTHING_FOUND_DEFAULT_MESSAGE
  }

  #handleKeydownOnPopover = (event) => {
    if (event.key === "Escape") {
      this.#hidePopover()
      this.#editorElement.focus()
      event.stopPropagation()
    } else if (this.#isCompletingPunctuation(event)) {
      this.#completeWithPunctuation(event)
    }
    // Arrow keys are handled via Lexical commands
  }

  #isCompletingPunctuation(event) {
    return COMPLETING_PUNCTUATION.includes(event.key) && event.key !== this.trigger && !event.isComposing && !this.#isShortcut(event)
  }

  // AltGr reports as Ctrl+Alt on Windows, but types characters rather than running shortcuts.
  #isShortcut(event) {
    return (event.ctrlKey || event.metaKey) && !event.getModifierState("AltGraph")
  }

  #completeWithPunctuation(event) {
    const filter = this.#editorContents.textBackUntil(this.trigger)

    if (!this.#selectedListItem || this.#isEmptyQueryPunctuation(filter, event.key)) {
      this.#hidePopover()
    } else if (this.#selectedOptionMatches(filter) && !this.source.anyCandidateMatches(filter + event.key)) {
      event.preventDefault()
      event.stopPropagation()
      this.#optionWasSelected(event.key)
    }
  }

  // Punctuation straight after the trigger is more often an emoticon, as in ":)",
  // than a pick, so it closes the prompt. A comma still picks, as it always has.
  #isEmptyQueryPunctuation(filter, key) {
    return !filter && key !== ","
  }

  #selectedOptionMatches(filter) {
    return filter === this.#shownFilter || this.source.listItemMatches(this.#selectedListItem, filter)
  }

  // Android Mobile keyboard doesn't trigger KEY_SPACE_COMMAND
  #handleInputCommand(event) {
    if (event.inputType === "insertText" && event.data === " ") return this.#handleSelectedOption(event)
  }

  #moveSelectionDown() {
    this.#moveSelectionBy(1)
  }

  #moveSelectionUp() {
    this.#moveSelectionBy(-1)
  }

  #moveSelectionBy(offset) {
    const items = this.#listItemElements

    if (items.length > 0) {
      const index = (this.#selectedIndex + offset + items.length) % items.length
      this.#selectOption(items[index], { scrollIntoView: true })
    }
  }

  get #selectedIndex() {
    return this.#listItemElements.findIndex((item) => item.hasAttribute("aria-selected"))
  }

  get #selectedListItem() {
    return this.#listItemElements[this.#selectedIndex]
  }

  #handleSelectedOption(event) {
    event.preventDefault()
    event.stopPropagation()
    this.#optionWasSelected()
    return true
  }

  #optionWasSelected(followedBy = "") {
    this.#replaceTriggerWithSelectedItem(followedBy)
    this.#hidePopover()
    this.#editorElement.focus()
  }

  #replaceTriggerWithSelectedItem(followedBy) {
    const promptItem = this.source.promptItemFor(this.#selectedListItem)

    if (!promptItem) { return }

    const templates = Array.from(promptItem.querySelectorAll("template[type='editor']"))
    const stringToReplace = `${this.trigger}${this.#editorContents.textBackUntil(this.trigger)}`

    if (this.hasAttribute("insert-editable-text")) {
      this.#insertTemplatesAsEditableText(templates, stringToReplace, { followedBy })
    } else {
      this.#insertTemplatesAsAttachments(templates, stringToReplace, { fallbackSgid: promptItem.getAttribute("sgid"), followedBy })
    }
  }

  #insertTemplatesAsEditableText(templates, stringToReplace, { followedBy }) {
    this.#editor.update(() => {
      const nodes = templates.flatMap(template => this.#buildEditableTextNodes(template))
      this.#editorContents.replaceTextBackUntil(stringToReplace, nodes, { followedBy })
    })
  }

  #buildEditableTextNodes(template) {
    return this.#editorElement.$generateNodesFromDOM(parseHtml(`${template.innerHTML}`))
  }

  #insertTemplatesAsAttachments(templates, stringToReplace, { fallbackSgid, followedBy }) {
    this.#editor.update(() => {
      const attachmentNodes = this.#buildAttachmentNodes(templates, fallbackSgid)
      const spacedAttachmentNodes = attachmentNodes.flatMap(node => [ node, this.#getSpacerTextNode() ]).slice(0, -1)
      this.#editorContents.replaceTextBackUntil(stringToReplace, spacedAttachmentNodes, { followedBy })
    })
  }

  #buildAttachmentNodes(templates, fallbackSgid = null) {
    return templates
      .filter(template => this.#editorElement.permitsAttachmentContentType(
        template.getAttribute("content-type") || this.#defaultPromptContentType))
      .map(template => this.#buildAttachmentNode(
        template.innerHTML,
        template.getAttribute("content-type") || this.#defaultPromptContentType,
        template.getAttribute("sgid") || fallbackSgid
      ))
  }

  #getSpacerTextNode() {
    return $createTextNode(" ")
  }

  get #defaultPromptContentType() {
    const attachmentContentTypeNamespace = Lexxy.global.get("attachmentContentTypeNamespace")
    return `application/vnd.${attachmentContentTypeNamespace}.${this.name}`
  }

  #buildAttachmentNode(innerHtml, contentType, sgid) {
    return new CustomActionTextAttachmentNode({ sgid, contentType, innerHtml })
  }

  get #editorContents() {
    return this.#editorElement.contents
  }

  get #editorContentElement() {
    return this.#editorElement.editorContentElement
  }

  #buildPopover(listItems) {
    const popoverContainer = createElement("ul", { role: "listbox", id: generateDomId("prompt-popover") }) // Avoiding [popover] due to not being able to position at an arbitrary X, Y position.
    popoverContainer.classList.add("lexxy-prompt-menu")
    popoverContainer.style.position = "absolute"
    popoverContainer.setAttribute("nonce", getNonce())
    popoverContainer.append(...listItems)
    popoverContainer.addEventListener("click", this.#handlePopoverClick)
    this.#editorElement.appendChild(popoverContainer)
    return popoverContainer
  }

  #handlePopoverClick = (event) => {
    const listItem = event.target.closest(".lexxy-prompt-menu__item")
    if (listItem) {
      this.#selectOption(listItem)
      this.#optionWasSelected()
    }
  }

  #reconnect() {
    this.disconnectedCallback()
    this.connectedCallback()
  }
}

export default LexicalPromptElement

function clipsDescendants(style) {
  return style.overflowY !== "visible" || /paint|strict|content/.test(style.contain) || style.contentVisibility === "auto"
}

// The bounding rect is in viewport coordinates, already scaled by any transform, while
// clientTop and clientHeight are untransformed layout sizes, so they're scaled to match.
function scrollportBounds(element) {
  const rect = element.getBoundingClientRect()
  const scale = rect.height / element.offsetHeight || 1
  const top = rect.top + element.clientTop * scale

  return { top, bottom: top + element.clientHeight * scale }
}

function containsFixedDescendants(style) {
  return [ style.transform, style.translate, style.rotate, style.scale, style.perspective, style.filter, style.backdropFilter ]
    .some(value => value && value !== "none") ||
    /transform|translate|rotate|scale|perspective|filter/.test(style.willChange) ||
    /paint|layout|strict|content/.test(style.contain) ||
    /size/.test(style.containerType) ||
    style.contentVisibility === "auto"
}
